import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import type { AskRedoStreamEvent } from "@redotech/merchant-sdk/rpc/schema/agents";
import { parseAskRedoTurnStatus } from "@redotech/merchant-sdk/rpc/schema/agents/ask-redo/ask-redo-turn-status";
import type { PageContext } from "@redotech/redo-agents-common/page-context";
import { abortRace } from "@redotech/util/abort";
import type { ChatTransport, UIMessageChunk } from "ai";
import { takeClientToolResults } from "../client-tools/client-tool-run-store";
import { withAskRedoConfirmationData } from "../confirmation-data";
import {
  extractApprovalResponse,
  extractLastUserMessage,
} from "../conversion/ui-message-to-redo";
import { extractUserReplyPayload } from "../escalation/extract-user-reply";
import type { AskRedoUIMessage } from "../types";

/**
 * Merchant-facing copy for non-server-origin errors. Mirrors the
 * server-only `ASK_REDO_GENERIC_ERROR_MESSAGE` (can't import it here).
 */
const ASK_REDO_CLIENT_GENERIC_ERROR_MESSAGE = "Please try again.";

/**
 * Marks a SERVER-emitted `error` stream event, whose copy is already
 * merchant-safe; the stream catch passes these through and masks the rest.
 */
class AskRedoServerStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AskRedoServerStreamError";
  }
}

export interface AskRedoReconnectPolicy {
  /** Wait before each consecutive reconnect; the last entry repeats. */
  delaysMs: readonly number[];
  /** Continuous failure while online after which the turn shows an error. */
  giveUpAfterMs: number;
}

const DEFAULT_RECONNECT_POLICY: AskRedoReconnectPolicy = {
  delaysMs: [250, 1000, 2000, 4000, 8000],
  giveUpAfterMs: 2 * 60 * 1000,
};

interface TurnPosition {
  threadId: string;
  turnId: string;
  /** Index of the next buffered turn event the server sends. */
  cursor: number;
}

interface TurnSession {
  turn: TurnPosition | undefined;
  /** False while a replay sends the kept backlog: its event count isn't the buffer position yet. */
  cursorTrusted: boolean;
  userStopped: boolean;
}

interface TurnConnection {
  events: AsyncIterable<AskRedoStreamEvent>;
  /** Opened from cursor 0 by `watchAskRedoTurn`, which replays only the kept backlog. */
  projects: boolean;
}

type ConnectionEnd =
  | { kind: "terminal" }
  | { kind: "restarted" }
  | { kind: "dropped"; error: unknown; progressed: boolean };

/**
 * Live read-out of the escalation context provided to the transport.
 * A getter (not a snapshot) so the transport sees the current status
 * each time `sendMessages` runs — the Chat instance is cached across
 * mounts, so a closure would freeze on the first mount's value.
 */
export interface EscalationSnapshot {
  status: "idle" | "waiting" | "human-active" | "closed";
  escalationId: string | null;
  /**
   * The triage thread id the escalation is bound to. The transport
   * uses this to scope diversion: we only divert a user submit to
   * Intercom when the CURRENT chat's id matches this bound thread.
   * Without that scoping, a merchant who escalates on thread A and
   * then opens unrelated thread B mid-escalation would have their
   * sends-from-B routed into A's Intercom conversation.
   */
  triageThreadId: string | null;
}

export type AskRedoEscalationCreatedEvent = Extract<
  AskRedoStreamEvent,
  { type: "emergency-escalation-created" }
>;

/**
 * Bridges Ask Redo's RPC streaming generator into the Vercel AI SDK
 * `ChatTransport` interface so `useChat` can drive the UI.
 *
 * Why a custom transport:
 *   - `DefaultChatTransport` assumes HTTP+SSE. Our merchant server's
 *     `LambdaRouter` can't stream — every streaming agent endpoint
 *     uses `userPermissionedRpc([]).generator<>()`. This is a
 *     first-class AI SDK extension point for exactly this case.
 *
 * Event translation (Redo → UIMessageChunk):
 *   - text-delta            → text-start / text-delta / text-end
 *   - tool-input-start      → tool-input-start (dynamic: true)
 *   - tool-input-delta      → tool-input-delta
 *   - tool-call             → tool-input-available (dynamic: true)
 *   - tool-approval-request → tool-input-available with confirmation metadata,
 *     then tool-approval-request. The approval chunk itself carries only ids,
 *     so tool-specific approval cards read details from the enriched input.
 *   - tool-output-available → tool-output-available (dynamic: true)
 *   - tool-output-error     → tool-output-error (dynamic: true)
 *   - tool-output-denied    → tool-output-denied
 *   - reasoning             → reasoning-start / -delta / -end
 *   - status                → (dropped — clients infer from state machine)
 *   - complete              → (emits finish; threadId captured for resume)
 *   - error                 → error
 *
 * A stream that starts with a `turn` status reconnects from its cursor
 * via `watchAskRedoTurn` until the turn's `complete` or `error`.
 *
 * Approval pause semantics:
 *   When the agent hits a `needsApproval` tool, the server emits
 *   `tool-approval-request` and the generator returns. The transport
 *   closes the stream cleanly. `useChat`'s
 *   `lastAssistantMessageIsCompleteWithApprovalResponses` then fires
 *   a new `sendMessages` carrying the merchant's decision; we
 *   forward it via `approvalResponse` so the handler can flip
 *   `PendingTool.confirmed` and resume.
 */
export interface AskRedoRpcTransportOptions {
  /** Called when a `turn` status or `complete` event delivers a threadId; at most
   *  once per `chatId` (URL sync, slide-out → full-page handoff). */
  onThreadIdCaptured?: (chatId: string, threadId: string) => void;

  /** Called at send time to pick up the latest page context. A getter
   *  (not a plain value) so the transport sees whatever page the
   *  merchant is on *right now* — the Chat instance is cached across
   *  mounts and the transport is built once, so a static value would
   *  freeze at construction. Returning `undefined` omits the field. */
  getPageContext?: () => PageContext | undefined;

  /** Getter for the merchant's IANA timezone, resolved at send time
   *  for the same reason as `getPageContext`. */
  getMerchantTimezone?: () => string | undefined;

  /** Live escalation snapshot. When status === "human-active" the
   *  transport sends the user's reply to Intercom via
   *  `sendEscalationMessage` instead of streaming the agent. */
  getEscalation?: () => EscalationSnapshot | undefined;

  /** Called when the server creates a support escalation for the
   *  thread: an approved handoff or an emergency-mode bypass. */
  onEmergencyEscalationCreated?: (event: AskRedoEscalationCreatedEvent) => void;

  /** Server cancelled a tool call parked on a PREVIOUS assistant message
   *  (see `resolvableToolCallIds` in `sendMessages`). */
  onStaleToolCancelled?: (args: {
    chatId: string;
    threadId: string | undefined;
    toolCallId: string;
    errorText: string;
  }) => void;

  /** The server restarted the chat's turn after a worker failure. The
   *  stream has finished; the caller reloads the thread and resumes. */
  onTurnRestarted?: (chatId: string, threadId: string) => void;

  reconnectPolicy?: AskRedoReconnectPolicy;
}

export class AskRedoRpcTransport implements ChatTransport<AskRedoUIMessage> {
  /**
   * Maps `chatId` → server-assigned `LlmThread._id` captured from the
   * first turn's `complete` event. Subsequent turns carry the
   * threadId in the RPC input so the server continues the same
   * thread rather than creating a new one each send.
   */
  private readonly threadIdByChatId = new Map<string, string>();

  /**
   * Maps `chatId` → a promise that resolves with the server threadId
   * once the FIRST thread-creating request for that chat reaches its
   * `complete` event.
   *
   * This is the structural guard against the runaway-new-thread class
   * of bug (2026-05-22: one browser opened 967 threads in 25s). A
   * brand-new conversation has no threadId until the server assigns
   * one asynchronously, and the server treats every request WITHOUT a
   * threadId as a brand-new conversation. So any code path that fires
   * `sendMessage` more than once before that first `complete` lands —
   * an effect re-firing, a double-submit, a retry — would mint a fresh
   * thread per call.
   *
   * The invariant we enforce here: for a given chat, only the FIRST
   * send may create a thread. While it's in flight, any concurrent
   * send awaits this promise and continues the SAME thread instead of
   * opening another. Resolves to `undefined` if the first request ends
   * without assigning a threadId (error / abort), so later sends can
   * legitimately try again rather than deadlock.
   */
  private readonly pendingThreadIdByChatId = new Map<
    string,
    Promise<string | undefined>
  >();

  private readonly sessionByChatId = new Map<string, TurnSession>();
  private readonly supersededFinishChatIds = new Set<string>();

  constructor(
    private readonly rpcClient: RedoMerchantRpcClient,
    private readonly options?: AskRedoRpcTransportOptions,
  ) {}

  getThreadIdForChat(chatId: string): string | undefined {
    return (
      this.threadIdByChatId.get(chatId) ??
      (isValidThreadId(chatId) ? chatId : undefined)
    );
  }

  private captureThreadId(chatId: string, threadId: string): void {
    const alreadyKnown = this.threadIdByChatId.has(chatId);
    this.threadIdByChatId.set(chatId, threadId);
    if (!alreadyKnown) {
      this.options?.onThreadIdCaptured?.(chatId, threadId);
    }
  }

  async requestEmergencyEscalation({
    chatId,
    abortSignal,
  }: {
    chatId: string;
    abortSignal?: AbortSignal;
  }): Promise<void> {
    const pageContext = this.options?.getPageContext?.();
    const merchantTimezone = this.options?.getMerchantTimezone?.();
    const threadId = this.getThreadIdForChat(chatId);
    const stream = this.rpcClient.streamAskRedo(
      {
        messages: [],
        threadId,
        emergencyEscalation: true,
        ...(pageContext ? { pageContext } : {}),
        ...(merchantTimezone ? { merchantTimezone } : {}),
      },
      { signal: abortSignal },
    );

    let escalationCreated = false;
    for await (const event of stream) {
      if (event.type === "complete" && event.threadId) {
        this.captureThreadId(chatId, event.threadId);
      }
      if (event.type === "emergency-escalation-created") {
        this.captureThreadId(chatId, event.threadId);
        this.options?.onEmergencyEscalationCreated?.(event);
        escalationCreated = true;
      }
      if (event.type === "error") {
        throw new Error(event.error);
      }
    }

    if (!escalationCreated) {
      throw new Error("Support handoff was not created.");
    }
  }

  async sendMessages(options: {
    chatId: string;
    messages: AskRedoUIMessage[];
    abortSignal?: AbortSignal;
    trigger: "submit-message" | "regenerate-message" | "resume-stream";
    messageId?: string;
    metadata?: unknown;
  }): Promise<ReadableStream<UIMessageChunk>> {
    const approvalResponse = extractApprovalResponse(options.messages);
    // Browser-executed tool results awaiting delivery to the agent.
    // Draining the store here (not in the predicate) guarantees each
    // result is sent exactly once even if sends overlap.
    const clientToolResults = takeClientToolResults(options.messages);

    // Active-escalation interception: when a human support agent is
    // engaged, the user's typing should reach Intercom — not the AI.
    // Approval responses still flow to the agent so the agent loop can
    // resolve any pending tool, but a plain user submit during human-
    // active gets diverted. The user message is already in the local
    // chat via useChat's submit; the SSE subscription will inject the
    // human's reply later.
    //
    // Divert is THREAD-SCOPED: we only intercept sends from the chat
    // bound to the escalation's triage thread. A merchant who escalates
    // on thread A and then browses to non-escalated thread B should
    // still talk to the AI on B — sends from B must NOT leak into A's
    // Intercom conversation.
    //
    // The catch: AI SDK's `Chat.id` is set at construction time and
    // does NOT change when we re-key the `chatInstances` cache from
    // `new-<uuid>` to the server threadId. So a fresh-chat → captured
    // threadId → escalated → human-active flow sees `options.chatId`
    // still equal to `new-<uuid>` while `escalation.triageThreadId`
    // is the server id. Compare against the EFFECTIVE thread id from
    // the transport's `threadIdByChatId` map (populated by
    // `onThreadIdCaptured`), falling back to `chatId` for deep-link
    // mounts where chatId IS already the server id.
    const escalation = this.options?.getEscalation?.();
    const effectiveThreadId = this.getThreadIdForChat(options.chatId);
    if (
      !approvalResponse &&
      options.trigger === "submit-message" &&
      escalation?.status === "human-active" &&
      escalation.escalationId &&
      escalation.triageThreadId &&
      effectiveThreadId !== undefined &&
      escalation.triageThreadId === effectiveThreadId
    ) {
      const last = options.messages[options.messages.length - 1];
      if (last?.role === "user") {
        return buildSyntheticIntercomReplyStream({
          rpcClient: this.rpcClient,
          escalationId: escalation.escalationId,
          message: last,
          abortSignal: options.abortSignal,
          messageId: options.messageId ?? crypto.randomUUID(),
        });
      }
    }

    // Resume-after-approval sends empty messages — the approval IS
    // the turn. Same for a resume that only delivers browser-executed
    // tool results (the conversation ends on an assistant message; re-
    // extracting the previous user message would duplicate it on the
    // thread). Otherwise send only the newest user message; the
    // server's `LlmThread` holds everything prior.
    const lastMessage = options.messages[options.messages.length - 1];
    const messages =
      approvalResponse ||
      (clientToolResults.length > 0 && lastMessage?.role !== "user")
        ? []
        : extractLastUserMessage(options.messages);

    // Prefer the server-assigned threadId we captured earlier. Fall
    // back to `chatId` if it happens to look like a Mongo ObjectId
    // (covers the deep-link case `/ask-redo/:threadId`).
    let threadId = this.getThreadIdForChat(options.chatId);

    // In-flight guard: if we don't have a threadId yet but a first
    // thread-creating request for this chat is already running, wait
    // for it and continue THAT thread rather than opening another.
    // This serialises the cold-start of a conversation so a re-firing
    // effect / double-submit can't fan one conversation into N
    // threads. Approval + resume triggers are exempt: they always
    // carry (or resolve) a threadId through their own paths.
    if (!threadId && options.trigger === "submit-message") {
      const inFlight = this.pendingThreadIdByChatId.get(options.chatId);
      if (inFlight) {
        threadId = await inFlight;
      }
    }

    // This send OWNS the cold-start if, after the await above, there's
    // still no threadId. It publishes the pending promise so siblings
    // that arrive while its stream is open will await instead of
    // creating their own thread. `resolvePending` is fired from the
    // stream below on `complete` (with the id) or on close (with
    // undefined) so the gate can never hang.
    const ownsColdStart = !threadId && options.trigger === "submit-message";
    let resolvePending: ((id: string | undefined) => void) | undefined;
    if (ownsColdStart) {
      this.pendingThreadIdByChatId.set(
        options.chatId,
        new Promise<string | undefined>((resolve) => {
          resolvePending = resolve;
        }),
      );
    }

    // AI SDK's reducer only binds tool-output chunks to the message being
    // assembled; output chunks for any other id throw and kill the stream.
    const resolvableToolCallIds = new Set<string>(
      lastMessage?.role === "assistant"
        ? lastMessage.parts.flatMap((part) =>
            part.type === "dynamic-tool" ? [part.toolCallId] : [],
          )
        : [],
    );

    const pageContext = this.options?.getPageContext?.();
    const merchantTimezone = this.options?.getMerchantTimezone?.();

    const rpcInput = {
      messages,
      threadId,
      ...(pageContext ? { pageContext } : {}),
      ...(merchantTimezone ? { merchantTimezone } : {}),
      ...(approvalResponse !== undefined ? { approvalResponse } : {}),
      ...(clientToolResults.length > 0 ? { clientToolResults } : {}),
    };

    return this.streamTurn({
      chatId: options.chatId,
      mode: "send",
      connect: (signal) => ({
        events: this.rpcClient.streamAskRedo(rpcInput, { signal }),
        projects: false,
      }),
      abortSignal: options.abortSignal,
      // Respect a caller-supplied messageId so the chunks we emit
      // share id with whatever record useChat may already have.
      startMessageId: options.messageId ?? crypto.randomUUID(),
      resolvableToolCallIds,
      threadId,
      onThreadId: (capturedThreadId) => {
        // Release the cold-start gate: any sibling send awaiting
        // this chat's first threadId now continues this thread.
        resolvePending?.(capturedThreadId);
        resolvePending = undefined;
      },
      onEnd: () => {
        // Ended without a threadId: waiting siblings retry instead of hanging.
        resolvePending?.(undefined);
        resolvePending = undefined;
        if (ownsColdStart) {
          this.pendingThreadIdByChatId.delete(options.chatId);
        }
      },
    });
  }

  /** Reattaches to the thread's running turn, replaying it into a message keyed by the turn. */
  async reconnectToStream(options: {
    chatId: string;
    abortSignal?: AbortSignal;
  }): Promise<ReadableStream<UIMessageChunk> | null> {
    const threadId = this.getThreadIdForChat(options.chatId);
    if (!threadId) {
      return null;
    }
    return this.streamTurn({
      chatId: options.chatId,
      mode: "reattach",
      connect: (signal) => ({
        events: this.rpcClient.watchAskRedoTurn({ threadId }, { signal }),
        projects: true,
      }),
      abortSignal: options.abortSignal,
      startMessageId: undefined,
      resolvableToolCallIds: new Set(),
      threadId,
    });
  }

  /** Stops the chat's turn for every tab; the local stream it ends never reconnects. */
  stopTurn(chatId: string): void {
    const session = this.sessionByChatId.get(chatId);
    if (session) {
      session.userStopped = true;
    }
    const turn = session?.turn;
    const threadId = turn?.threadId ?? this.getThreadIdForChat(chatId);
    if (!threadId) {
      return;
    }
    this.rpcClient
      .stopAskRedoTurn({
        threadId,
        ...(turn
          ? {
              turnId: turn.turnId,
              ...(session?.cursorTrusted ? { cursor: turn.cursor } : {}),
            }
          : {}),
      })
      .catch((error: unknown) => {
        console.error("[ask-redo] stop turn failed", { threadId }, error);
      });
  }

  /** True once after a stream ended because its turn restarted: the reload replaces its reply. */
  takeSupersededFinish(chatId: string): boolean {
    return this.supersededFinishChatIds.delete(chatId);
  }

  private streamTurn(args: {
    chatId: string;
    mode: "send" | "reattach";
    connect: (signal: AbortSignal) => TurnConnection;
    abortSignal: AbortSignal | undefined;
    /** Undefined: start once the first `turn` status names the turn. */
    startMessageId: string | undefined;
    resolvableToolCallIds: Set<string>;
    threadId: string | undefined;
    onThreadId?: (threadId: string) => void;
    onEnd?: () => void;
  }): ReadableStream<UIMessageChunk> {
    const { chatId, mode, resolvableToolCallIds } = args;
    const policy = this.options?.reconnectPolicy ?? DEFAULT_RECONNECT_POLICY;
    // The RPC `signal` aborts only the network call; `cancel()` below also
    // stops it when the consumer closes the reader.
    const cancelController = new AbortController();
    const signal = args.abortSignal
      ? abortRace([args.abortSignal, cancelController.signal])
      : cancelController.signal;
    const session: TurnSession = {
      turn: undefined,
      cursorTrusted: true,
      userStopped: false,
    };
    this.sessionByChatId.set(chatId, session);
    // Enqueue/close after the stream closed or was cancelled throws TypeError.
    let closed = false;

    let connectionController = new AbortController();
    const openConnection = (): TurnConnection => {
      connectionController = new AbortController();
      const connectionSignal = abortRace([signal, connectionController.signal]);
      const turn = session.turn;
      if (!turn) {
        return args.connect(connectionSignal);
      }
      return {
        events: this.rpcClient.watchAskRedoTurn(
          { threadId: turn.threadId, turnId: turn.turnId, cursor: turn.cursor },
          { signal: connectionSignal },
        ),
        projects: turn.cursor === 0,
      };
    };
    // A connection that outlived an offline spell may be silently dead.
    const onlineTarget = typeof window === "undefined" ? undefined : window;
    const onOnline = () => {
      if (session.turn) {
        connectionController.abort();
      }
    };

    return new ReadableStream<UIMessageChunk>({
      start: async (controller) => {
        const enqueue = (chunk: UIMessageChunk) => {
          if (!closed) {
            controller.enqueue(chunk);
          }
        };
        const close = () => {
          if (!closed) {
            closed = true;
            controller.close();
          }
        };

        let started = false;
        const ensureStarted = (messageId: string) => {
          if (started) {
            return;
          }
          started = true;
          enqueue({ type: "start", messageId });
          enqueue({ type: "start-step" });
        };
        if (args.startMessageId !== undefined) {
          ensureStarted(args.startMessageId);
        }

        let currentTextId: string | undefined;
        let currentReasoningId: string | undefined;
        const closeOpenText = () => {
          if (currentTextId) {
            enqueue({ type: "text-end", id: currentTextId });
            currentTextId = undefined;
          }
        };
        const closeOpenReasoning = () => {
          if (currentReasoningId) {
            enqueue({ type: "reasoning-end", id: currentReasoningId });
            currentReasoningId = undefined;
          }
        };
        const captureThreadId = (threadId: string) => {
          this.captureThreadId(chatId, threadId);
          args.onThreadId?.(threadId);
        };
        const translateContext: TranslateContext = {
          canResolveToolCall: (toolCallId) =>
            resolvableToolCallIds.has(toolCallId),
          markToolCallResolvable: (toolCallId) => {
            resolvableToolCallIds.add(toolCallId);
          },
          onStaleToolCancelled: (toolCallId, errorText) => {
            this.options?.onStaleToolCancelled?.({
              chatId,
              threadId: args.threadId,
              toolCallId,
              errorText,
            });
          },
          openReasoning: () => {
            if (!currentReasoningId) {
              currentReasoningId = crypto.randomUUID();
              enqueue({ type: "reasoning-start", id: currentReasoningId });
            }
            return currentReasoningId;
          },
          closeOpenReasoning,
          openText: () => {
            if (!currentTextId) {
              currentTextId = crypto.randomUUID();
              enqueue({ type: "text-start", id: currentTextId });
            }
            return currentTextId;
          },
          closeOpenText,
        };

        const readConnection = async ({
          events,
          projects,
        }: TurnConnection): Promise<ConnectionEnd> => {
          let turnStatuses = 0;
          let progressed = false;
          let restartSeen = false;
          try {
            for await (const event of events) {
              const control =
                event.type === "status"
                  ? parseAskRedoTurnStatus(event.status)
                  : undefined;
              if (control?.kind === "turn") {
                session.cursorTrusted = !projects || turnStatuses > 0;
                turnStatuses += 1;
                session.turn = {
                  threadId: control.threadId,
                  turnId: control.turnId,
                  cursor: control.cursor,
                };
                captureThreadId(control.threadId);
                ensureStarted(`ask-redo-turn-${control.turnId}`);
                continue;
              }
              progressed = true;
              if (session.turn) {
                session.turn.cursor += 1;
              }
              if (control?.kind === "restarted") {
                restartSeen = true;
                continue;
              }
              // A retry that cannot resume ends the turn with an error right after the restart.
              if (restartSeen && event.type !== "error") {
                return { kind: "restarted" };
              }
              if (event.type === "complete" && event.threadId) {
                captureThreadId(event.threadId);
              }
              if (event.type === "emergency-escalation-created") {
                captureThreadId(event.threadId);
                this.options?.onEmergencyEscalationCreated?.(event);
              }
              translate(event, enqueue, translateContext);
              if (session.turn && event.type === "complete") {
                return { kind: "terminal" };
              }
            }
          } catch (error) {
            if (
              error instanceof AskRedoServerStreamError ||
              (!session.turn && mode === "send")
            ) {
              throw error;
            }
            if (restartSeen) {
              return { kind: "restarted" };
            }
            return { kind: "dropped", error, progressed };
          }
          if (restartSeen) {
            return { kind: "restarted" };
          }
          return session.turn
            ? { kind: "dropped", error: undefined, progressed }
            : { kind: "terminal" };
        };

        let restarted = false;
        onlineTarget?.addEventListener("online", onOnline);
        try {
          let connection = openConnection();
          let failures = 0;
          let failingSince: number | undefined;
          for (;;) {
            const end = await readConnection(connection);
            if (end.kind === "terminal") {
              break;
            }
            if (end.kind === "restarted") {
              restarted = true;
              break;
            }
            if (signal.aborted || session.userStopped) {
              break;
            }
            // A drop mid-replay leaves no usable cursor; reload like a restart.
            if (!session.cursorTrusted) {
              restarted = true;
              break;
            }
            if (end.error === undefined || end.progressed) {
              failures = 0;
              failingSince = undefined;
            }
            if (end.error !== undefined) {
              failingSince = isOnline()
                ? (failingSince ?? Date.now())
                : undefined;
              if (
                failingSince !== undefined &&
                Date.now() - failingSince >= policy.giveUpAfterMs
              ) {
                throw end.error;
              }
            }
            const delayMs =
              policy.delaysMs[Math.min(failures, policy.delaysMs.length - 1)] ??
              0;
            failures += 1;
            console.warn("[ask-redo] reconnecting to turn", {
              chatId,
              turnId: session.turn?.turnId,
              cursor: session.turn?.cursor,
              failures,
              error: end.error,
            });
            await waitToReconnect(delayMs, signal);
            if (signal.aborted || session.userStopped) {
              break;
            }
            connection = openConnection();
          }
          closeOpenText();
          closeOpenReasoning();
          if (started) {
            enqueue({ type: "finish-step" });
            enqueue({ type: "finish" });
          }
          if (restarted) {
            this.supersededFinishChatIds.add(chatId);
          }
        } catch (err) {
          // Close open blocks before the error chunk, then finish so useChat
          // leaves `status: "streaming"`.
          closeOpenText();
          closeOpenReasoning();
          // Keep the raw error for Datadog RUM breadcrumbs.
          console.error("[ask-redo] stream error", err);
          // A reattach that never found its turn has no message to show an error on.
          if (started) {
            // Server copy is merchant-safe → pass through; mask everything else.
            const errorText =
              err instanceof AskRedoServerStreamError
                ? err.message
                : ASK_REDO_CLIENT_GENERIC_ERROR_MESSAGE;
            enqueue({ type: "error", errorText });
            enqueue({ type: "finish-step" });
            enqueue({ type: "finish" });
          }
        } finally {
          onlineTarget?.removeEventListener("online", onOnline);
          args.onEnd?.();
          if (this.sessionByChatId.get(chatId) === session) {
            this.sessionByChatId.delete(chatId);
          }
          close();
          const restartedThreadId = restarted
            ? session.turn?.threadId
            : undefined;
          if (restartedThreadId) {
            this.options?.onTurnRestarted?.(chatId, restartedThreadId);
          }
        }
      },
      cancel: () => {
        closed = true;
        cancelController.abort();
      },
    });
  }
}

function isOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

/** Resolves after `ms`, as soon as the browser comes back online, or on abort. */
function waitToReconnect(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const onlineTarget = typeof window === "undefined" ? undefined : window;
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      onlineTarget?.removeEventListener("online", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener("abort", done, { once: true });
    onlineTarget?.addEventListener("online", done);
  });
}

interface TranslateContext {
  openText: () => string;
  closeOpenText: () => void;
  openReasoning: () => string;
  closeOpenReasoning: () => void;
  /** True when the reducer's current message can bind this tool call id. */
  canResolveToolCall: (toolCallId: string) => boolean;
  markToolCallResolvable: (toolCallId: string) => void;
  onStaleToolCancelled: (toolCallId: string, errorText: string) => void;
}

type EnqueueFn = (chunk: UIMessageChunk) => void;

function translate(
  event: AskRedoStreamEvent,
  enqueue: EnqueueFn,
  ctx: TranslateContext,
): void {
  switch (event.type) {
    case "text-delta": {
      // Any open reasoning block closes when prose starts — matches
      // how most chat UIs collapse the "thinking" panel.
      ctx.closeOpenReasoning();
      const id = ctx.openText();
      enqueue({ type: "text-delta", id, delta: event.textDelta });
      return;
    }
    case "reasoning": {
      const id = ctx.openReasoning();
      enqueue({ type: "reasoning-delta", id, delta: event.text });
      return;
    }
    case "tool-input-start": {
      ctx.closeOpenText();
      ctx.markToolCallResolvable(event.toolCallId);
      // `dynamic: true` → the AI SDK reducer materialises a
      // `DynamicToolUIPart` (type: "dynamic-tool", runtime toolName).
      // Ask Redo discovers tools at runtime; there's no compile-time
      // tool map for useChat to narrow against, so this is the
      // canonical fit.
      enqueue({
        type: "tool-input-start",
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        dynamic: true,
      });
      return;
    }
    case "tool-input-delta": {
      enqueue({
        type: "tool-input-delta",
        toolCallId: event.toolCallId,
        inputTextDelta: event.inputTextDelta,
      });
      return;
    }
    case "tool-call": {
      ctx.closeOpenText();
      ctx.markToolCallResolvable(event.toolCallId);
      enqueue({
        type: "tool-input-available",
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        input: event.input,
        dynamic: true,
      });
      return;
    }
    case "tool-approval-request": {
      ctx.closeOpenText();
      ctx.markToolCallResolvable(event.toolCallId);
      // The approval chunk shape doesn't carry toolName/input; the AI SDK
      // reducer reads them off the existing part. Re-emit input first so
      // tool-specific cards can read the server-side confirmation payload.
      enqueue({
        type: "tool-input-available",
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        input: withAskRedoConfirmationData(
          event.input,
          event.confirmationData,
          event.prompt,
        ),
        dynamic: true,
      });
      enqueue({
        type: "tool-approval-request",
        approvalId: event.toolCallId,
        toolCallId: event.toolCallId,
      });
      return;
    }
    case "tool-output-available": {
      if (!ctx.canResolveToolCall(event.toolCallId)) {
        console.warn("[ask-redo] dropped tool output for a stale tool call", {
          toolCallId: event.toolCallId,
          toolName: event.toolName,
        });
        return;
      }
      enqueue({
        type: "tool-output-available",
        toolCallId: event.toolCallId,
        output: event.output,
        dynamic: true,
      });
      return;
    }
    case "tool-output-error": {
      if (!ctx.canResolveToolCall(event.toolCallId)) {
        ctx.onStaleToolCancelled(event.toolCallId, event.errorText);
        return;
      }
      enqueue({
        type: "tool-output-error",
        toolCallId: event.toolCallId,
        errorText: event.errorText,
        dynamic: true,
      });
      return;
    }
    case "tool-output-denied": {
      if (!ctx.canResolveToolCall(event.toolCallId)) {
        ctx.onStaleToolCancelled(
          event.toolCallId,
          "This tool call was denied.",
        );
        return;
      }
      enqueue({ type: "tool-output-denied", toolCallId: event.toolCallId });
      return;
    }
    case "status": {
      // Clients infer status from the tool state machine.
      return;
    }
    case "emergency-escalation-created": {
      // The option callback updates EscalationContext; no chat
      // message is rendered for the emergency handoff itself.
      return;
    }
    case "complete": {
      // Finish is emitted by the caller once the generator closes.
      // Complete's role is to deliver the server-assigned threadId,
      // which the caller already extracted before calling translate.
      return;
    }
    case "error": {
      // Route through the stream catch so open blocks close and the
      // message is recognised as server-safe (passed through, not masked).
      throw new AskRedoServerStreamError(event.error);
    }
    default: {
      // Exhaustiveness check — adding a new event type to
      // `AskRedoStreamEvent` will surface as a type error here.
      const _exhaustive: never = event;
      void _exhaustive;
    }
  }
}

/**
 * Forward a user reply to Intercom during an active escalation. Emits
 * a no-op stream so useChat transitions cleanly out of `streaming`
 * status — the actual human reply will land via the Notice SSE
 * subscription, NOT this stream.
 *
 * If the RPC fails we emit an `error` chunk so useChat surfaces it via
 * `chat.error`; useChat keeps the user's message in the local list
 * either way (the chat instance manages that — the transport just
 * acks the network attempt).
 */
function buildSyntheticIntercomReplyStream(args: {
  rpcClient: RedoMerchantRpcClient;
  escalationId: string;
  message: AskRedoUIMessage;
  abortSignal: AbortSignal | undefined;
  messageId: string;
}): ReadableStream<UIMessageChunk> {
  const { rpcClient, escalationId, message, abortSignal, messageId } = args;
  const { text, attachmentUrls } = extractUserReplyPayload(message);

  return new ReadableStream<UIMessageChunk>({
    async start(controller) {
      let closed = false;
      let assistantStarted = false;
      const safeEnqueue = (chunk: UIMessageChunk) => {
        if (!closed) {
          controller.enqueue(chunk);
        }
      };
      const safeClose = () => {
        if (closed) {
          return;
        }
        closed = true;
        controller.close();
      };
      // We do NOT emit `{ type: "start", messageId }` on success.
      // AI SDK commits an assistant message into the chat the moment
      // `start` lands with a messageId, even with no subsequent
      // content — every Intercom-routed user reply would leave an
      // empty assistant bubble underneath. Start chunks are emitted
      // ONLY on the error path so the failure UI has somewhere to
      // attach. On success, we close the stream with zero chunks:
      // the merchant's outgoing message is already in `chat.messages`
      // (useChat optimistically appended it), and the support reply
      // will arrive separately via the SSE subscription as a new
      // assistant message.
      const startErrorAssistant = () => {
        if (assistantStarted) {
          return;
        }
        assistantStarted = true;
        safeEnqueue({ type: "start", messageId });
        safeEnqueue({ type: "start-step" });
      };
      const finishErrorAssistant = () => {
        if (!assistantStarted) {
          return;
        }
        safeEnqueue({ type: "finish-step" });
        safeEnqueue({ type: "finish" });
      };

      try {
        if (!text && attachmentUrls.length === 0) {
          // Empty user submit (shouldn't normally happen — the input
          // box guards against it — but defend anyway).
          return;
        }

        const result = await rpcClient.sendEscalationMessage(
          {
            escalationId,
            message: text,
            ...(attachmentUrls.length > 0 && { attachmentUrls }),
          },
          { signal: abortSignal },
        );

        if (!result.success) {
          console.error(
            "[ask-redo] sendEscalationMessage returned success:false",
            { escalationId },
          );
          startErrorAssistant();
          safeEnqueue({
            type: "error",
            errorText:
              "We couldn't deliver your message to the support team. Please try again.",
          });
        }
      } catch (err) {
        console.error(
          "[ask-redo] sendEscalationMessage threw — user reply may not have reached Intercom",
          { escalationId },
          err,
        );
        startErrorAssistant();
        safeEnqueue({
          type: "error",
          errorText: err instanceof Error ? err.message : String(err),
        });
      } finally {
        finishErrorAssistant();
        safeClose();
      }
    },
  });
}

function isValidThreadId(value: string | undefined): value is string {
  if (!value) {
    return false;
  }
  // Server-issued ids are Mongo ObjectIds — 24 hex chars. This
  // rejects the client-generated `new-<uuid>` IDs that useChat
  // hands us before the first `complete` event lands. Note: the
  // server enforces team ownership independently, so a malicious
  // 24-hex-char chatId is still safe — this is a UX guard.
  return /^[0-9a-f]{24}$/i.test(value);
}
